import {createAsyncThunk, createSlice, isFulfilled, isPending, isRejected} from '@reduxjs/toolkit';
import {RootState} from '../../../Config/Store';
import axios from 'axios';
import type {AxiosResponse} from 'axios';
import {serializeAxiosError} from "../../../Config/Reducers/ReducerUtils";
import {DefaultState} from "../../../Config/Types";
import {LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";
import {PagedSearchParams, Period} from "@atiautomacao/ati-ui-library";
import {MenuItemStatus} from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";

export type PowerStationCommunicationState =
    | 'NO_COMMUNICATION'
    | 'COMMUNICATING'
    | 'SYNCHRONIZING';

export type PowerStationAlarmSummary = {
    critical: number;
    nonCritical: number;
    equipmentNoCommunication: number;
};

export const emptyPowerStationAlarmSummary = (): PowerStationAlarmSummary => ({
    critical: 0,
    nonCritical: 0,
    equipmentNoCommunication: 0,
});

export class Chart {
    dateTime: string;
    value: number;

    constructor(dateTime: string, value: number) {
        this.dateTime = dateTime;
        this.value = value;
    }
}

export class Performance {

    powerStationId: number;
    generatedEnergySum: number;
    irradianceSum: number;
    powerStationName: string;
    powerCapacityNominal: number;
    performanceRatio: number;
    instantPower: number;
    instantIrradiance: number;
    injectedEnergySum: number;
    expectedEnergyCorrectedByIrradiationSum: number | null;
    availability: number;
    status: MenuItemStatus;
    instantPowerList: Chart[];
    maxPower: number;
    communicationState?: PowerStationCommunicationState;
    alarmSummary?: PowerStationAlarmSummary;

    constructor(powerStationId: number, generatedEnergySum: number, irradianceSum: number, powerStationName: string, powerCapacityNominal: number, performanceRatio: number, instantPower: number, instantIrradiance: number, injectedEnergySum: number, expectedEnergyCorrectedByIrradiationSum: number | null, availability: number, status: MenuItemStatus, instantPowerList: Chart[], maxPower: number, communicationState?: PowerStationCommunicationState, alarmSummary?: PowerStationAlarmSummary) {
        this.generatedEnergySum = generatedEnergySum;
        this.irradianceSum = irradianceSum;
        this.powerStationName = powerStationName;
        this.powerCapacityNominal = powerCapacityNominal;
        this.performanceRatio = performanceRatio;
        this.instantPower = instantPower;
        this.instantIrradiance = instantIrradiance;
        this.injectedEnergySum = injectedEnergySum;
        this.expectedEnergyCorrectedByIrradiationSum = expectedEnergyCorrectedByIrradiationSum;
        this.availability = availability;
        this.status = status;
        this.powerStationId = powerStationId;
        this.instantPowerList = instantPowerList;
        this.maxPower = maxPower;
        this.communicationState = communicationState;
        this.alarmSummary = alarmSummary;
    }
}

export type PowerStationPerformanceRow = Pick<Performance,
    | 'powerStationId'
    | 'powerStationName'
    | 'powerCapacityNominal'
    | 'instantPower'
    | 'instantIrradiance'
    | 'generatedEnergySum'
    | 'injectedEnergySum'
    | 'expectedEnergyCorrectedByIrradiationSum'
    | 'availability'
    | 'performanceRatio'
> & {
    communicationState: PowerStationCommunicationState;
    alarmSummary: PowerStationAlarmSummary;
};

type PowerStationPerformancePage = {
    data: PowerStationPerformanceRow[];
    totalOfPages: number;
    totalOfRecords: number;
    totalOfRequestedRecords: number;
    pageNumber: number;
    recordsPerPage: number;
};

interface PerformanceAnalysisDefaultState extends DefaultState {
    powerStations: PowerStationPerformanceRow[];
    activeAnalysisRequestId: string | null;
    latestListRequestId?: string;
}

const initialState: PerformanceAnalysisDefaultState = {
    loading: false,
    saving: false,

    errorMessage: undefined,
    successMessage: undefined,

    powerStations: new Array<PowerStationPerformanceRow>(),
    entities: new Array<Performance>(),
    entity: undefined,

    totalOfPages: 0,
    totalOfRecords: 0,
    totalOfRequestedRecords: 0,
    pageNumber: 0,
    recordsPerPage: 20,
    activeAnalysisRequestId: null,
    latestListRequestId: undefined,
};

const apiUrl = 'api/power-station/performance-analysis';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPowerStationCommunicationState(value: unknown): value is PowerStationCommunicationState {
    return value === 'NO_COMMUNICATION' || value === 'COMMUNICATING' || value === 'SYNCHRONIZING';
}

function isAlarmCount(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isPowerStationAlarmSummary(value: unknown): value is PowerStationAlarmSummary {
    return isRecord(value)
        && isAlarmCount(value.critical)
        && isAlarmCount(value.nonCritical)
        && isAlarmCount(value.equipmentNoCommunication);
}

function isPowerStationPerformanceRow(value: unknown): value is PowerStationPerformanceRow {
    return isRecord(value)
        && isPowerStationCommunicationState(value.communicationState)
        && isPowerStationAlarmSummary(value.alarmSummary);
}

function isPowerStationPerformancePage(value: unknown): value is PowerStationPerformancePage {
    return isRecord(value)
        && Array.isArray(value.data)
        && value.data.every(isPowerStationPerformanceRow)
        && typeof value.totalOfPages === 'number'
        && typeof value.totalOfRecords === 'number'
        && typeof value.totalOfRequestedRecords === 'number'
        && typeof value.pageNumber === 'number'
        && typeof value.recordsPerPage === 'number';
}


export const searchEntities = createAsyncThunk<
    any,
    {
        period: Period;
        actualDay: Period;
        params: PagedSearchParams | undefined;
    }
>(
    'performance Analysis/fetch_entity_list',
    async ({period, actualDay, params}) => {
        return axios.get(
            apiUrl + `/comparison?startDateTime=${LocalDateTimeFormatISO(period.fromDateTime ? period.fromDateTime : new Date())}&endDateTime=${LocalDateTimeFormatISO(period.toDateTime ? period.toDateTime : new Date())}&startActualDay=${LocalDateTimeFormatISO(actualDay.fromDateTime ? actualDay.fromDateTime : new Date())}&dateNow=${LocalDateTimeFormatISO(actualDay.toDateTime ? actualDay.toDateTime : new Date())}`,
            {
                params: params ? params.toURLSearchParams() : null
            }
        );

    },
    {serializeError: serializeAxiosError}
);

export const searchAnalisysData = createAsyncThunk<
    any,
    {
        powerStationIds: number[];
        period: Period;
        actualDay: Period;
    }
>(
    'performance Analysis/fetch_data_list',
    async ({powerStationIds, period, actualDay}) => {
        return axios.post(
            apiUrl + `/data?startDateTime=${LocalDateTimeFormatISO(period.fromDateTime ? period.fromDateTime : new Date())}&endDateTime=${LocalDateTimeFormatISO(period.toDateTime ? period.toDateTime : new Date())}&startActualDay=${LocalDateTimeFormatISO(actualDay.fromDateTime ? actualDay.fromDateTime : new Date())}&dateNow=${LocalDateTimeFormatISO(actualDay.toDateTime ? actualDay.toDateTime : new Date())}`,
            {
                powerStationIds: powerStationIds
            }
        );

    },
    {serializeError: serializeAxiosError}
);


export const searchPowerStations = createAsyncThunk<
    PowerStationPerformancePage,
    {
        period: Period;
        actualDay: Period;
        params: PagedSearchParams | undefined;
    }
>(
    'performance Analysis/fetch_power_station_list',
    async ({period, actualDay, params}) => {
        const response: AxiosResponse<unknown> = await axios.get(
            apiUrl + `?startDateTime=${LocalDateTimeFormatISO(period.fromDateTime ? period.fromDateTime : new Date())}&endDateTime=${LocalDateTimeFormatISO(period.toDateTime ? period.toDateTime : new Date())}&startActualDay=${LocalDateTimeFormatISO(actualDay.fromDateTime ? actualDay.fromDateTime : new Date())}&dateNow=${LocalDateTimeFormatISO(actualDay.toDateTime ? actualDay.toDateTime : new Date())}`,
            {
                params: params ? params.toURLSearchParams() : null
            }
        );

        if (!isPowerStationPerformancePage(response.data)) {
            throw new Error('Performance analysis response is incomplete.');
        }

        return response.data;

    },
    {serializeError: serializeAxiosError}
);


export const getEntitiesWithoutParams = createAsyncThunk(
    "performanceAnalysisForMapsOverview/fetch_entity_list",
    async () => {
        const now = new Date();

        // Formato da data: yyyy-MM-ddTHH:mm:ss
        const startDate = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}T00:00:00`;
        const endDate = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}T23:59:59`;

        return axios.get(apiUrl + `/comparison?startDateTime=${startDate}&endDateTime=${endDate}`);
    }

);
export const getEntity = createAsyncThunk<any, any, { rejectValue: any }>(
    'performance/fetch_entity',
    async () => {
        return axios.get<Performance>(`${apiUrl}`);
    },
    {serializeError: serializeAxiosError}
);
// Slices
const PerformanceAnalysisReducer = createSlice({
    name: 'PerformanceAnalysisReducer',
    initialState,
    reducers: {
        /**
         * Reset the entity state to initial state
         */
        reset() {
            return initialState;
        },
        clear: (state) => {
            state.entity = {
                powerStationId: 0,
                irradianceSum: 0,
                generatedEnergySum: 0,
                injectedEnergySum: 0,
                expectedEnergyCorrectedByIrradiationSum: null,
                generatedEnergyList: 0,
                irradianceList: 0,
                powerStationName: "",
                powerCapacityNominal: 0,
                performanceRatio: 0,
                instantPower: 0,
                instantIrradiance: 0,
                injectedEnergyList: 0,
                availability: 0,
                status: "",
                instantPowerList: 0
            };
            state.entities = [];
            state.powerStations = [];
        },
    },
    extraReducers: (builder) => {
        // Add reducers for additional action types here, and handle loading state as needed
        builder
            .addCase(getEntity.fulfilled, (state, action) => {
                state.loading = false;
                state.entity = action.payload.data.data;
            })
            .addCase(searchAnalisysData.pending, (state, action) => {
                state.activeAnalysisRequestId = action.meta.requestId;
                state.loading = true;
                state.errorMessage = undefined;
            })
            .addCase(searchAnalisysData.fulfilled, (state, action) => {
                if (state.activeAnalysisRequestId !== action.meta.requestId) {
                    return;
                }
                state.loading = false;
                state.entities = action.payload.data;
            })
            .addCase(searchAnalisysData.rejected, (state, action) => {
                if (state.activeAnalysisRequestId !== action.meta.requestId) {
                    return;
                }
                state.loading = false;
            })
            .addMatcher(isFulfilled(searchEntities, getEntity, getEntitiesWithoutParams), (state, action) => {
                return {
                    ...state,
                    loading: false,
                    entities: action.payload.data,

                    totalOfPages: action.payload.data.totalOfPages,
                    totalOfRecords: action.payload.data.totalOfRecords,
                    totalOfRequestedRecords: action.payload.data.totalOfRequestedRecords,
                    pageNumber: action.payload.data.pageNumber,
                    recordsPerPage: action.payload.data.recordsPerPage,

                }
            })
            .addMatcher(isFulfilled(searchPowerStations), (state, action) => {
                if (action.meta.requestId !== state.latestListRequestId) {
                    return state;
                }
                return {
                    ...state,
                    loading: false,
                    powerStations: action.payload.data,
                    entities: [],

                    totalOfPages: action.payload.totalOfPages,
                    totalOfRecords: action.payload.totalOfRecords,
                    totalOfRequestedRecords: action.payload.totalOfRequestedRecords,
                    pageNumber: action.payload.pageNumber,
                    recordsPerPage: action.payload.recordsPerPage,

                };
            })
            .addMatcher(isPending(searchPowerStations), (state, action) => {
                state.errorMessage = undefined;
                state.loading = true;
                state.latestListRequestId = action.meta.requestId;
            })
            .addMatcher(isPending(searchEntities, getEntity, getEntitiesWithoutParams), state => {
                state.errorMessage = undefined;
                state.loading = true;
            })
            .addMatcher(isRejected(searchEntities, getEntity, getEntitiesWithoutParams), state => {
                return {
                    ...state,
                    loading: false,
                };
            })
            .addMatcher(isRejected(searchPowerStations), (state, action) => {
                if (action.meta.requestId !== state.latestListRequestId) {
                    return state;
                }
                return {
                    ...state,
                    loading: false,
                    errorMessage: "Erro ao carregar usinas da Visão Diária.",
                };
            })
    },
})

export const {reset,clear} = PerformanceAnalysisReducer.actions;
export const selectPerformanceAnalysis = (state: RootState) => state.performanceAnalysis;

// Reducer
export default PerformanceAnalysisReducer.reducer;
